/**
 * MobileSidebarDrawer — drag-up bottom sheet that surfaces the same nav
 * items as the desktop Sidebar for screens < 900px (where the desktop
 * `aside[data-testid="sidebar"]` is `display:none`).
 *
 * UX
 * --
 *   • Peek mode: fixed 56px-tall pill anchored to the bottom of the
 *     viewport. Shows a grab handle + page title + chevron-up.
 *   • Drag up (or tap): the sheet expands to ~80vh and renders the nav
 *     items (Pinned + More + Scrapers children).
 *   • Drag down (or tap backdrop / chevron): collapses back to peek.
 *   • Honours `user.module_grants` so cliente accounts only see granted
 *     modules — mirrors the desktop Sidebar rules.
 *   • Powered by framer-motion's drag with `dragConstraints` + a
 *     velocity-aware close threshold so flick gestures feel native.
 *
 * Mounted globally from `App.js`. CSS rule in `index.css` only renders
 * this component below 900px via the parent wrapper class.
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useDragControls } from "framer-motion";
import {
  ChevronUp, ChevronDown, Menu, X,
  LayoutDashboard, KanbanSquare, Landmark, Radar, Target, Search,
  Mail, BarChart3, TrendingUp, Gauge, Inbox, Plug, Webhook, BookOpen,
  Instagram, Linkedin, Youtube,
  Shield, Sparkles, Zap, Send, Upload, Users, Car,
} from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { useLang } from "../i18n/LangContext";
import { isItemGranted } from "../auth/moduleMap";

// Mirror of the Sidebar.jsx item lists kept LOCAL so the mobile drawer
// can evolve independently if the layout calls for it. Keep IDs in sync
// with the desktop sidebar to share `MODULE_MAP` semantics.
const PINNED = [
  { id: "agency-dashboard",  labelKey: "sidebar.agency_dashboard",  icon: LayoutDashboard, route: "/dashboard" },
  { id: "kanban",            labelKey: "sidebar.kanban",            icon: KanbanSquare,    route: "/kanban" },
  { id: "gov-kanban",        labelKey: "sidebar.gov_kanban",        icon: Landmark,        route: "/gov-kanban", badge: "GOV" },
  { id: "public-works",      labelKey: "sidebar.public_works",      icon: Landmark,        route: "/public-works" },
  { id: "gc-leaderboard",    labelKey: "sidebar.gc_leaderboard",    icon: Landmark,        route: "/gc-leaderboard" },
  { id: "b2c-intent",        labelKey: "sidebar.b2c_intent",        icon: Radar,           route: "/b2c-intent" },
  { id: "b2c-radar",         labelKey: "sidebar.b2c_radar",         icon: Radar,           route: "/b2c-radar" },
  { id: "lead-gap",          labelKey: "sidebar.lead_gap",          icon: Target,          route: "/lead-gap", badge: "NEW" },
];

const SCRAPERS = [
  { id: "instagram", labelKey: "sidebar.instagram", icon: Instagram, route: "/instagram" },
  { id: "linkedin",  labelKey: "sidebar.linkedin",  icon: Linkedin,  route: "/linkedin" },
  { id: "youtube",   labelKey: "sidebar.youtube",   icon: Youtube,   route: "/youtube" },
];

// Iter274 · Smith Protective — master-only. Mirrors the Sidebar.jsx
// hierarchy but flattened to fit the mobile drawer list surface.
// Every item is masterOnly so isModuleGranted keeps it hidden from
// non-master tenants; the section itself only renders when isMaster.
const SMITH = [
  { id: "smith-hq",         label: "Command HQ",     icon: Shield,    route: "/smith/hq",              masterOnly: true, badge: "EXEC" },
  { id: "smith-ask",        label: "Ask Bar",        icon: Sparkles,  route: "/smith/ask",             masterOnly: true, badge: "AI" },
  // Iter291 · Vehicle Security promoted next to Command HQ / Ask Bar.
  { id: "vehicle-security", label: "Vehicle Security", icon: Car,     route: "/smith/vehicle-security", masterOnly: true, badge: "NEW" },
  { id: "smith-ai-search",  label: "AI Lead Search", icon: Sparkles,  route: "/smith/ai-search",       masterOnly: true, badge: "AI" },
  { id: "smith-ai-signals", label: "Signals",        icon: Zap,       route: "/smith/ai-search/signals", masterOnly: true, badge: "AI" },
  { id: "smith-prospects",  label: "Prospects",      icon: Target,    route: "/smith/prospects",       masterOnly: true },
  { id: "smith-icps",       label: "ICPs",           icon: Users,     route: "/smith/icps",            masterOnly: true, badge: "NEW" },
  { id: "smith-imports",    label: "Imports",        icon: Upload,    route: "/smith/imports",         masterOnly: true, badge: "NEW" },
  { id: "smith-outreach",   label: "Outreach",       icon: Send,      route: "/smith/outreach",        masterOnly: true, badge: "NEW" },
  { id: "smith-map",        label: "Territory Map",  icon: Radar,     route: "/smith/map",             masterOnly: true },
];

const MORE = [
  { id: "prospecting",    labelKey: "sidebar.prospecting",    icon: Search,     route: "/prospecting" },
  { id: "campaigns",      labelKey: "sidebar.campaigns",      icon: Mail,       route: "/campaigns" },
  { id: "insights",       labelKey: "sidebar.insights",       icon: BarChart3,  route: "/insights" },
  { id: "roi",            labelKey: "sidebar.roi",            icon: TrendingUp, route: "/roi" },
  { id: "marketing-apis", labelKey: "sidebar.marketing_apis", icon: Gauge,      route: "/marketing-apis" },
  { id: "replies",        labelKey: "sidebar.replies",        icon: Inbox,      route: "/replies" },
  { id: "integrations",   labelKey: "sidebar.integrations",   icon: Plug,       route: "/integrations" },
  { id: "webhooks",       labelKey: "sidebar.webhooks",       icon: Webhook,    route: "/webhooks" },
  { id: "library",        labelKey: "sidebar.library",        icon: BookOpen,   route: "/library" },
];

const PEEK_HEIGHT = 56;          // px — visible portion when collapsed
const EXPANDED_VH = 78;           // viewport-height % when expanded
const FLICK_VELOCITY = 600;       // px/s — flick threshold to toggle

export default function MobileSidebarDrawer() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const controls = useDragControls();

  const isAuthed = !!(user && user.email);
  const isMaster = !!(user && (user.role === "super_admin" || user.role === "admin" || user.role === "master_demo"));
  const isCliente = !!(user && (user.role === "cliente_admin" || user.role === "cliente_user"));
  const grants = user?.module_grants || null;

  const isGranted = (it) =>
    isItemGranted(it.id, {
      isMaster, isCliente, tenantModuleGrants: grants,
    });

  const sections = useMemo(() => ({
    pinned: PINNED.filter(isGranted),
    scrapers: SCRAPERS.filter(isGranted),
    smith: isMaster ? SMITH.filter(isGranted) : [],
    more: MORE.filter(isGranted),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [grants, isMaster, isCliente]);

  // Auto-collapse on navigation so the user lands on the new page.
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Lock body scroll while expanded so the drawer feels modal.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!isAuthed) return null;

  const currentLabel = (() => {
    const all = [...PINNED, ...SCRAPERS, ...SMITH, ...MORE];
    const hit = all.find((x) => location.pathname.startsWith(x.route));
    if (hit) return hit.labelKey ? t(hit.labelKey) : hit.label;
    return t("sidebar.agency_dashboard");
  })();

  const onDragEnd = (_e, info) => {
    const { offset, velocity } = info;
    // Open if user swiped up far enough OR flicked up fast.
    if (offset.y < -80 || velocity.y < -FLICK_VELOCITY) setOpen(true);
    // Close if user swiped down OR flicked down fast.
    else if (offset.y > 100 || velocity.y > FLICK_VELOCITY) setOpen(false);
  };

  const goto = (route) => {
    setOpen(false);
    navigate(route);
  };

  return (
    <>
      {/* Backdrop */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-sidebar-backdrop"
            className="lg:hidden fixed inset-0 z-[44] bg-black/55 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
        )}
      </AnimatePresence>

      {/* Drawer — visible only on screens that hide the desktop sidebar (< lg) */}
      <motion.aside
        data-testid="mobile-sidebar-drawer"
        data-open={open ? "true" : "false"}
        role="navigation"
        aria-label={t("mobile.nav_label")}
        drag="y"
        dragControls={controls}
        dragListener={false}
        dragConstraints={{ top: 0, bottom: 0 }}
        dragElastic={0.18}
        onDragEnd={onDragEnd}
        initial={false}
        animate={{ y: open ? 0 : `calc(100% - ${PEEK_HEIGHT}px)` }}
        transition={{ type: "spring", stiffness: 360, damping: 36 }}
        className="lg:hidden fixed inset-x-0 bottom-0 z-[45] rounded-t-2xl text-white shadow-[0_-12px_40px_-12px_rgba(0,0,0,0.7)] border-t border-white/10"
        style={{
          height: `${EXPANDED_VH}vh`,
          background: "linear-gradient(180deg, #1A1A2E 0%, #110F22 100%)",
          touchAction: "none",
          // Respect iPhone home-indicator / notch safe areas.
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
        }}
      >
        {/* Drag handle / peek bar — also serves as the tap-to-toggle button */}
        <button
          type="button"
          data-testid="mobile-sidebar-handle"
          aria-expanded={open}
          aria-controls="mobile-sidebar-body"
          onPointerDown={(e) => controls.start(e)}
          onClick={() => setOpen((o) => !o)}
          className="w-full flex items-center gap-3 px-4 select-none"
          style={{ height: PEEK_HEIGHT, cursor: "grab" }}
        >
          <div className="w-9 h-1.5 rounded-full bg-white/30 shrink-0" aria-hidden="true" />
          <span className="inline-flex items-center gap-2 text-[13px] font-semibold tracking-tight">
            <Menu className="w-4 h-4 text-[#C9A24E]" />
            {currentLabel}
          </span>
          <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-white/60">
            {open ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
          </span>
        </button>

        {/* Body */}
        <div id="mobile-sidebar-body"
             className="overflow-y-auto px-3 pb-6"
             style={{ height: `calc(${EXPANDED_VH}vh - ${PEEK_HEIGHT}px)`, touchAction: "pan-y" }}>
          <div className="flex items-center justify-between px-1 pt-1 pb-3">
            <div className="text-[11px] uppercase tracking-[0.18em] text-white/45 font-bold">{t("sidebar.footer") || "Navigation"}</div>
            <button
              type="button"
              data-testid="mobile-sidebar-close"
              onClick={() => setOpen(false)}
              className="w-8 h-8 inline-flex items-center justify-center rounded-md text-white/60 hover:text-white hover:bg-white/5"
              aria-label={t("mobile.close_menu")}
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <DrawerSection items={sections.pinned} active={location.pathname} onGo={goto} />
          {sections.scrapers.length > 0 && (
            <>
              <SectionTitle title={t("mobile.scrapers_title")} />
              <DrawerSection items={sections.scrapers} active={location.pathname} onGo={goto} />
            </>
          )}
          {sections.smith.length > 0 && (
            <>
              <SectionTitle title={t("mobile.smith_title")} accent="#DC2626" />
              <DrawerSection items={sections.smith} active={location.pathname} onGo={goto} />
            </>
          )}
          {sections.more.length > 0 && (
            <>
              <SectionTitle title={t("sidebar.more") || "More"} />
              <DrawerSection items={sections.more} active={location.pathname} onGo={goto} />
            </>
          )}

          <div className="mt-5 px-2 py-3 rounded-lg bg-white/[0.04] text-[11px] text-white/55 flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-[#C9A24E] text-[#0A1233] flex items-center justify-center text-[10px] font-bold">
              {(user.name || user.email).slice(0, 1).toUpperCase()}
            </div>
            <div className="leading-tight min-w-0">
              <div className="text-white font-semibold truncate text-[12px]">{user.name || user.email.split("@")[0]}</div>
              <div className="truncate">{user.email}</div>
            </div>
          </div>
        </div>
      </motion.aside>
    </>
  );
}

function SectionTitle({ title, accent }) {
  return (
    <div className="mt-4 mb-1.5 px-1 text-[10px] uppercase tracking-[0.18em] font-bold flex items-center gap-1.5"
         style={{ color: accent || "rgba(255,255,255,0.4)" }}>
      {accent && <span className="w-1.5 h-1.5 rounded-full" style={{ background: accent }} />}
      {title}
    </div>
  );
}

function DrawerSection({ items, active, onGo }) {
  return (
    <ul className="space-y-0.5">
      {items.map((it) => {
        const Icon = it.icon;
        const isActive = active === it.route || active.startsWith(it.route + "/");
        return (
          <li key={it.id}>
            <button
              type="button"
              data-testid={`mobile-nav-${it.id}`}
              onClick={() => onGo(it.route)}
              className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left text-[13px] transition-colors ${
                isActive
                  ? "bg-[#C9A24E]/15 text-white border border-[#C9A24E]/40"
                  : "text-white/80 hover:bg-white/5 border border-transparent"
              }`}
            >
              <Icon className="w-4 h-4 shrink-0" />
              <span className="flex-1 truncate">
                {it.labelKey ? <Translated k={it.labelKey} /> : (it.label || it.id)}
              </span>
              {it.badge && (
                <span className="text-[9.5px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-[#C9A24E] text-[#0A1233]">
                  {it.badge}
                </span>
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function Translated({ k }) {
  const { t } = useLang();
  return <>{t(k)}</>;
}
